import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Circle, ArrowRight, Phone } from "lucide-react";
import { toast } from "sonner";

/**
 * Candidate onboarding checklist — 4 steps rendered on the candidate Overview.
 * Also auto-opens a lightweight phone-prompt modal on first sign-in if phone is missing.
 */
export default function OnboardingChecklist({ applicationsCount = 0 }) {
  const { user, setUser } = useAuth();
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const dismissedKey = `rvm-phone-dismissed-${user?.user_id}`;

  // Auto-open the phone modal once per session if candidate has no phone
  useEffect(() => {
    if (user?.role !== "candidate") return;
    if (user?.phone) return;
    if (sessionStorage.getItem(dismissedKey)) return;
    const t = setTimeout(() => setPhoneOpen(true), 800);
    return () => clearTimeout(t);
  }, [user, dismissedKey]);

  const savePhone = async () => {
    const clean = phone.trim();
    if (clean && !/^[\d+\-\s()]{7,20}$/.test(clean)) {
      return toast.error("Please enter a valid phone number");
    }
    setSaving(true);
    try {
      const res = await api.patch("/auth/profile", { phone: clean });
      setUser(res.data.user);
      toast.success("Phone saved — thanks!");
      setPhoneOpen(false);
    } catch (e) {
      toast.error("Could not save your phone");
    } finally { setSaving(false); }
  };

  const skip = () => {
    sessionStorage.setItem(dismissedKey, "1");
    setPhoneOpen(false);
  };

  const steps = [
    {
      key: "phone",
      label: "Complete your profile",
      hint: "Add a phone number so our team can reach you.",
      done: !!user?.phone,
      cta: { label: "Add phone", onClick: () => setPhoneOpen(true), testid: "onb-add-phone" },
    },
    {
      key: "apply",
      label: "Apply to your first role",
      hint: applicationsCount > 0 ? `You've submitted ${applicationsCount} application${applicationsCount === 1 ? "" : "s"}.` : "Browse open roles and apply — takes under a minute.",
      done: applicationsCount > 0,
      cta: { to: "/dashboard/applications", label: "My applications", testid: "onb-apply" },
    },
    {
      key: "consult",
      label: "Book your career consultation",
      hint: "A complimentary 30-minute session with a Ravemora mentor.",
      done: false,
      cta: { to: "/contact", label: "Reach out", testid: "onb-book" },
    },
    {
      key: "rcp",
      label: "Explore the RCP Program",
      hint: "See how Ravemora Certified Professional shapes your career trajectory.",
      done: false,
      cta: { to: "/dashboard/rcp", label: "Open RCP", testid: "onb-rcp" },
    },
  ];

  const completed = steps.filter(s => s.done).length;
  const total = steps.length;

  return (
    <>
      <div className="border border-navy/10 bg-white" data-testid="onboarding-checklist">
        <div className="px-6 py-5 border-b border-navy/10 flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="label-eyebrow text-gold">Getting started</div>
            <h2 className="serif-display text-2xl md:text-3xl mt-1 text-navy leading-tight">Your onboarding, in four steps.</h2>
          </div>
          <div className="text-sm text-navy/60"><span className="serif-display text-navy text-2xl">{completed}</span> of {total} complete</div>
        </div>
        <ul>
          {steps.map((s, i) => (
            <li key={s.key} className={`px-6 py-5 border-b border-navy/10 last:border-b-0 flex items-start gap-4 ${s.done ? "bg-slateBg/40" : ""}`} data-testid={`onb-step-${i+1}`}>
              <div className="mt-1">
                {s.done ? (
                  <CheckCircle2 size={22} className="text-gold" />
                ) : (
                  <Circle size={22} className="text-navy/25" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`serif-display text-lg leading-tight ${s.done ? "text-navy/50 line-through decoration-1" : "text-navy"}`}>
                  <span className="text-gold mr-2 text-sm serif-display">{String(i+1).padStart(2, "0")}</span>{s.label}
                </div>
                <div className="text-sm text-navy/60 mt-1">{s.hint}</div>
              </div>
              <div className="shrink-0">
                {!s.done && (
                  s.cta.to ? (
                    <Link to={s.cta.to} data-testid={s.cta.testid} className="btn-outline !px-3 !py-1.5 !text-[11px] inline-flex items-center gap-1">
                      {s.cta.label} <ArrowRight size={12} />
                    </Link>
                  ) : (
                    <button onClick={s.cta.onClick} data-testid={s.cta.testid} className="btn-outline !px-3 !py-1.5 !text-[11px] inline-flex items-center gap-1">
                      {s.cta.label} <ArrowRight size={12} />
                    </button>
                  )
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
        <DialogContent className="max-w-md" data-testid="phone-modal">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl flex items-center gap-2">
              <Phone size={20} className="text-gold" /> One quick thing
            </DialogTitle>
            <DialogDescription>
              Add your phone number so a Ravemora mentor can reach you for your complimentary career consultation. We won't share it or spam you.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Label>Phone number</Label>
            <Input
              type="tel"
              autoFocus
              placeholder="+91 98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1"
              data-testid="phone-modal-input"
            />
          </div>
          <DialogFooter>
            <button onClick={skip} className="btn-outline" data-testid="phone-modal-skip">Later</button>
            <button onClick={savePhone} className="btn-primary" disabled={saving || !phone.trim()} data-testid="phone-modal-save">
              {saving ? "Saving..." : "Save phone"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
